import toast from 'react-hot-toast';
import { Switch } from '../../../components';
import { useAuthStore } from '../../../stores';
import { useClerkFactsSettings, useUpdateClerkFactsSettings } from '../hooks/useSettings';

/**
 * Shown on the Clerk integration card. Lets an admin share payment facts from
 * customer conversations ("says they transferred ₦85k for #1042") with Clerk,
 * which uses them when reconciling the bank statement. Only the one-sentence
 * fact is shared, never the conversation. See docs/clerk-memory-facts.md.
 */
export default function ClerkFactsToggle() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const { data } = useClerkFactsSettings();
  const update = useUpdateClerkFactsSettings();

  if (!data) return null;

  const onChange = async (next: boolean) => {
    try {
      await update.mutateAsync(next);
      toast.success(
        next ? 'Sharing payment facts with Clerk' : 'Stopped sharing payment facts with Clerk'
      );
    } catch {
      // useUpdateClerkFactsSettings already shows the error.
    }
  };

  return (
    <div className="mt-4 flex items-start justify-between gap-4 border-t border-line pt-4">
      <div>
        <p className="text-sm font-medium text-ink">Share payment facts with Clerk</p>
        <p className="mt-1 text-xs text-ink-2">
          When customers mention payments, promises to pay, refunds or whose account money comes
          from, a one-sentence summary goes to Clerk to help match bank transactions. Conversations
          themselves are never shared.
        </p>
        {!isAdmin && <p className="mt-1 text-xs text-ink-3">Only admins can change this.</p>}
      </div>
      <Switch
        checked={data.enabled}
        onChange={onChange}
        disabled={!isAdmin || update.isPending || (!data.enabled && !data.clerkConnected)}
        ariaLabel="Share payment facts with Clerk"
      />
    </div>
  );
}
